import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  CardHeadeWithActions,
  ConfirmDelete,
  MwButton,
  MwInputText,
  MwSpinnerButton,
  NoDataFound,
  Pagination,
} from "../components/ui";
import { useTranslation } from "react-i18next";
import { BalancePopup } from "../popups/BalancePopup";
import BalanceContext from "../utils/BalanceHalper";
import AccountContext from "../utils/AccountsHelper";
import i18next from "i18next";

export const OpeningBalance = () => {
  const [t] = useTranslation("global");
  const [isOpen, setIsOpen] = useState(false);
  const [actionData, setActionData] = useState({
    mode: null,
    balanceId: null,
    balances: null,
  });
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("selectedPeriod") || "{}");
    } catch {
      return {};
    }
  });
  const [accountFilter, setAccountFilter] = useState("");
  const { balances, pagination, fetchBalances, loading, deleteBalance } =
    useContext(BalanceContext);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const { accounts } = useContext(AccountContext);
  const [id, setId] = useState(null);
  const moneyFormatter = useMemo(() => {
    const locale = i18next.language === "ar" ? "en-US" : "en-US";
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [i18next.language]);
  const formatAmount = (value) => {
    if (value === null || value === undefined) return "-";
    const raw = typeof value === "string" ? value.trim() : value;
    if (raw === "") return "-";
    const normalized = typeof raw === "string" ? raw.replace(/,/g, "") : raw;
    const numeric = Number(normalized);
    if (!Number.isFinite(numeric)) return "-";
    return moneyFormatter.format(numeric);
  };

  const resolvePeriodClosed = (period) => {
    const rawStatus = period?.is_close ?? period?.is_closed;
    if (rawStatus !== undefined && rawStatus !== null && rawStatus !== "") {
      if (typeof rawStatus === "string") {
        return rawStatus === "1" || rawStatus.toLowerCase() === "true";
      }
      return rawStatus === 1 || rawStatus === true;
    }
    const closedUntil = String(period?.closeduntil || "").toLowerCase();
    return closedUntil === "closed";
  };

  useEffect(() => {
    const syncSelectedPeriod = () => {
      try {
        const stored = JSON.parse(localStorage.getItem("selectedPeriod") || "{}");
        setSelectedPeriod(stored || {});
      } catch {
        setSelectedPeriod({});
      }
    };
    const handlePeriodChange = () => syncSelectedPeriod();
    window.addEventListener("periodChanged", handlePeriodChange);
    return () => window.removeEventListener("periodChanged", handlePeriodChange);
  }, []);

  const isPeriodClosed = resolvePeriodClosed(selectedPeriod);

  const filteredBalances = useMemo(() => {
    const query = String(accountFilter || "").trim();
    if (!query) return balances;
    return balances.filter((row) => row?.account_no.includes(query));
  }, [accountFilter, balances]);

  const totalPages = pagination?.last_page || 1;
  const currentPage = pagination?.current_page || 1;

  const handlePageChange = async (page) => {
    if (page < 1 || page > totalPages) return;
    await fetchBalances({
      page,
      perPage: pagination?.per_page,
    });
  };

  return (
    <section className="p-2 font-normal">
      <CardHeadeWithActions
        title={t("menu.opening-balance")}
        icon={"/assets/icons/account-balance-dark.png"}
      >
        <MwButton
          classNameCustom={`w-max text-white bg-[#261759] hover:bg-[#463977] ${
            isPeriodClosed ? "opacity-50 cursor-not-allowed" : ""
          }`}
          children={t("openingBalance.add")}
          type="newBtn"
          disabled={isPeriodClosed}
          onClick={() => {
            if (isPeriodClosed) return;
            setActionData({ mode: "ADD", balanceId: null, balances: balances });
            setIsOpen(true);
          }}
        />
      </CardHeadeWithActions>

      {/* <div className="flex flex-wrap items-center gap-4 mt-4">
          <div className="w-[20%] min-w-[180px]">
            <MwInputText
              label={t("openingBalance.accountNumber")}
              inputType="text"
              value={accountFilter}
              placeholder={t("openingBalance.accountNumber")}
              onChange={(e) => setAccountFilter(e.target.value)}
            />
          </div>
      </div> */}

      <div className="w-full mt-3">
        {loading ? (
          <div className="w-full h-[30vh] flex justify-center items-center">
            <MwSpinnerButton />
          </div>
        ) : filteredBalances.length === 0 ? (
          <div className="w-[50%] mx-auto mt-36 flex justify-center items-center">
            <NoDataFound msg={t("openingBalance.noData")} />
          </div>
        ) : (
          <table className="w-full table-fixed">
            <thead className="bg-[#ebf9d9]">
              <tr className="text-center text-sm text-[#261759]">
                <th className="p-2 font-normal w-[140px] whitespace-nowrap">
                  {t("openingBalance.accountNumber")}
                </th>
                <th className="p-2 font-normal text-start">
                  {t("openingBalance.accountName")}
                </th>
                <th className="p-2 font-normal w-[120px] whitespace-nowrap">
                  {t("openingBalance.debit")}
                </th>
                <th className="p-2 font-normal w-[120px] whitespace-nowrap">
                  {t("openingBalance.credit")}
                </th>
                <th className="p-2 font-normal w-[120px] whitespace-nowrap">
                  {t("openingBalance.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredBalances.map((row, index) => {
                const accountNo = row.account_no;
                const findAccountRecursive = (accounts, accountNo) => {
                  for (const acc of accounts) {
                    if (acc.account_no === accountNo) return acc;

                    if (acc.childs?.length) {
                      const found = findAccountRecursive(acc.childs, accountNo);
                      if (found) return found;
                    }
                  }
                  return null;
                };

                const account = findAccountRecursive(accounts, accountNo);

                const accountName =
                  i18next.language === "ar"
                    ? account?.name_ar || "-"
                    : account?.name_en || "-";

                return (
                  <tr
                    key={row?.id ?? `${accountNo}-${index}`}
                    className="hover:bg-slate-200 border-b text-sm transition-all"
                  >
                    <td className="p-2 text-center text-[#261759] w-[140px] whitespace-nowrap">
                      {accountNo || "-"}
                    </td>
                    <td className="p-2 text-start text-[#261759] break-words">
                      {accountName || "-"}
                    </td>
                    <td className="p-2 text-center text-[#261759] w-[120px] whitespace-nowrap">
                      {formatAmount(row.de)}
                    </td>
                    <td className="p-2 text-center text-[#261759] w-[120px] whitespace-nowrap">
                      {formatAmount(row.cr)}
                    </td>
                    <td className="p-2 text-center text-[#261759] w-[120px] whitespace-nowrap">
                      <div className="flex justify-center gap-2">
                        <button
                          type="secondary"
                          disabled
                          className="opacity-50 cursor-not-allowed"
                          onClick={() => {
                            setActionData({
                              mode: "EDIT",
                              balanceId: row.id,
                              balances: row,
                            });
                            setIsOpen(true);
                          }}
                        >
                          <img
                            src="/assets/icons/Edit.png"
                            alt=""
                            className="w-[13px]"
                          />
                        </button>
                        <button
                          type="secondary"
                          onClick={async () => {
                            setId(row.id);
                            setConfirmDeleteOpen(true);
                          }}
                        >
                          <img
                            src="/assets/icons/Trash.png"
                            alt=""
                            className="w-[13px]"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
      <BalancePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        actionData={actionData}
        loading={loading}
      />
      <ConfirmDelete
        type={"delete"}
        onDeleteHandler={async () => {
          await deleteBalance(id);
          setConfirmDeleteOpen(false);
        }}
        onShow={confirmDeleteOpen}
        setOnShow={setConfirmDeleteOpen}
        msg={t("openingBalance.deleteConfirmMessage")}
        loading={loading}
      />
    </section>
  );
};
